import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface Platform {
  name: string;
  display_name: string;
  config_schema: Record<string, unknown>;
}

export type JobStatus = 'queued' | 'pending' | 'running' | 'paused' | 'cancelled' | 'done' | 'failed';

export type ScraperJobKind = 'manual' | 'scheduled' | 'probe';

export interface ScraperJob {
  id: string;
  platform: string;
  description?: string;
  phase: 'destinations' | 'reviews' | 'review_counts';
  status: JobStatus;
  kind?: ScraperJobKind;
  progress: {
    phase: string;
    found: number;
    saved: number;
    merged?: number;
    skipped?: number;
    failed: number;
    pct: number;
    total_steps?: number;
    current_step?: number;
    step_label?: string;
    destinations_total?: number;
  } | null;
  error_message?: string;
  created_at: string;
  updated_at: string;
  duration_s: number | null;
}

export interface JobLog {
  level: 'info' | 'warn' | 'error';
  message: string;
  created_at: string;
}

export interface CreateJobPayload {
  platform: string;
  phase: 'destinations' | 'reviews' | 'review_counts';
  config: { urls?: string[]; limit?: number; destination_ids?: string[]; [key: string]: unknown };
  description: string;
}

export interface BulkReviewJob {
  platform: string;
  job_id: string;
  status: 'running' | 'queued';
  destination_count: number;
}

export interface TravelokaCity {
  eid: string;
  name: string;
  label: string;
  emoji: string;
}

export interface TravelokaActivityType {
  value: string;
  label: string;
  icon: string;
}

export interface TravelokaCatalog {
  cities: TravelokaCity[];
  activity_types: TravelokaActivityType[];
}

export interface IpSession {
  ip: string;
  country_code: string;
  country_name: string;
  flag_emoji: string;
  recorded_at: string;
}

export type HealthStatus = 'online' | 'offline' | 'unknown' | 'degraded';

export interface ScraperHealth {
  id: string;
  status: HealthStatus;
  busy: boolean;
  last_checked_at: string | null;
  last_probe_at: string | null;
  consecutive_failures: number;
  consecutive_successes: number | null;
  last_error?: string | null;
  alerting: boolean;
  probe_job_id: string | null;
  probe_status: string | null;
  uptime_seconds?: number | null;
  version: number;
  updated_at: string | null;
}

export interface ScraperHealthResponse {
  platforms: ScraperHealth[];
  overall: { healthy: boolean; alerting: boolean };
}

export type SchedulePhase = 'destinations' | 'reviews';
export type ScheduleKind = 'scheduled' | 'probe';
export type ScheduleRunStatus = 'running' | 'done' | 'failed' | null;

export interface ScraperSchedule {
  platform: string;
  enabled: boolean;
  time: string;
  phase: SchedulePhase;
  kind: ScheduleKind;
  probe_url: string | null;
  next_run: string | null;
  last_run: string | null;
  last_run_status: ScheduleRunStatus;
  last_job_id: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduleUpdateBody {
  enabled?: boolean;
  time?: string;
  phase?: SchedulePhase;
  kind?: ScheduleKind;
}

export interface ScraperContainerState {
  platform: string;
  container: string;
  state: string;
  running: boolean;
}

export interface ScraperControlStatus {
  /** True when every scraper container is running. */
  enabled: boolean;
  containers: ScraperContainerState[];
  scheduler_enabled: boolean;
  docker_control_enabled: boolean;
  docker_available: boolean;
}

export interface ScraperControlResponse {
  ok: boolean;
  action?: 'start' | 'stop';
  platform?: string | null;
  errors: string[];
  status: ScraperControlStatus;
}

@Injectable({ providedIn: 'root' })
export class ScraperApiService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/scraper`;

  getPlatforms(): Observable<Platform[]> {
    return this.http.get<Platform[]>(`${this.base}/platforms`);
  }

  createJob(payload: CreateJobPayload): Observable<{ job_id: string; status: 'running' | 'queued' }> {
    return this.http.post<{ job_id: string; status: 'running' | 'queued' }>(`${this.base}/jobs`, payload);
  }

  createBulkReviewJobs(destinationIds: string[]): Observable<{ jobs: BulkReviewJob[] }> {
    return this.http.post<{ jobs: BulkReviewJob[] }>(`${this.base}/jobs/bulk-reviews`, {
      destination_ids: destinationIds,
    });
  }

  listJobs(
    skip = 0,
    limit = 20,
    filters?: { status?: string[]; platform?: string[]; phase?: string[] },
  ): Observable<{ jobs: ScraperJob[]; total: number; status_counts: Record<string, number> }> {
    let params = new HttpParams().set('skip', skip).set('limit', limit);
    for (const s of filters?.status ?? [])   params = params.append('status', s);
    for (const p of filters?.platform ?? []) params = params.append('platform', p);
    for (const ph of filters?.phase ?? [])   params = params.append('phase', ph);
    return this.http.get<{ jobs: ScraperJob[]; total: number; status_counts: Record<string, number> }>(`${this.base}/jobs`, { params });
  }

  getJob(jobId: string): Observable<ScraperJob> {
    return this.http.get<ScraperJob>(`${this.base}/jobs/${jobId}`);
  }

  cancelJob(jobId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/jobs/${jobId}/stop`, {});
  }

  deleteJob(jobId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/jobs/${jobId}`);
  }

  bulkDeleteJobs(ids: string[]): Observable<{ deleted: number }> {
    return this.http.post<{ deleted: number }>(`${this.base}/jobs/bulk-delete`, { ids });
  }

  bulkCancelJobs(ids: string[]): Observable<{ cancelled: number }> {
    return this.http.post<{ cancelled: number }>(`${this.base}/jobs/bulk-cancel`, { ids });
  }

  cleanupOldJobs(): Observable<{ deleted: number; cutoff_date: string }> {
    return this.http.post<{ deleted: number; cutoff_date: string }>(`${this.base}/jobs/cleanup-old`, {});
  }

  getJobLogs(jobId: string, skip = 0, limit = 200): Observable<JobLog[]> {
    const params = new HttpParams().set('skip', skip).set('limit', limit);
    return this.http.get<{ logs: JobLog[]; total: number }>(`${this.base}/jobs/${jobId}/logs`, { params })
      .pipe(map(r => r.logs ?? []));
  }

  // ── Traveloka discovery ──────────────────────────────────────────────────

  getTravelokaCatalog(): Observable<TravelokaCatalog> {
    return this.http.get<TravelokaCatalog>(`${this.base}/traveloka/catalog`);
  }

  discoverTraveloka(eid: string, activityType: string, limit = 0): Observable<{ job_id: string; status: string; url: string }> {
    const params = new HttpParams()
      .set('eid', eid)
      .set('activity_type', activityType)
      .set('limit', String(limit));
    return this.http.post<{ job_id: string; status: string; url: string }>(
      `${this.base}/traveloka/discover`, {}, { params }
    );
  }

  discoverAllCategories(eid: string, limit = 0): Observable<{ job_id: string; status: string; url_count: number }> {
    const params = new HttpParams().set('eid', eid).set('limit', String(limit));
    return this.http.post<{ job_id: string; status: string; url_count: number }>(
      `${this.base}/traveloka/discover-all-categories`, {}, { params }
    );
  }

  // ── Tiket discovery ──────────────────────────────────────────────────────

  discoverAllTiketCategories(publicId = 'indonesia', limit = 0): Observable<{ job_id: string; status: string; url_count: number }> {
    const params = new HttpParams().set('public_id', publicId).set('limit', String(limit));
    return this.http.post<{ job_id: string; status: string; url_count: number }>(
      `${this.base}/tiket/discover-all-categories`, {}, { params }
    );
  }

  getIpSessions(platform: string): Observable<IpSession[]> {
    return this.http.get<IpSession[]>(`${this.base}/ip-sessions/${platform}`);
  }

  resolveCaptcha(jobId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/jobs/${jobId}/resume`, {});
  }

  // ── Health monitoring ───────────────────────────────────────────────────

  getScraperHealth(): Observable<ScraperHealthResponse> {
    return this.http.get<ScraperHealthResponse>(`${this.base}/health`);
  }

  getPlatformHealth(platform: string): Observable<ScraperHealth> {
    return this.http.get<ScraperHealth>(`${this.base}/health/${platform}`);
  }

  triggerProbe(platform: string): Observable<{ message: string; platform: string; job_id: string }> {
    return this.http.post<{ message: string; platform: string; job_id: string }>(
      `${this.base}/health/${platform}/probe`, {},
    );
  }

  // ── Container control ───────────────────────────────────────────────────

  getScraperControl(): Observable<ScraperControlStatus> {
    return this.http.get<ScraperControlStatus>(`${this.base}/control`);
  }

  setScraperControl(enabled: boolean, platform?: string): Observable<ScraperControlResponse> {
    const body: { enabled: boolean; platform?: string } = { enabled };
    if (platform) body.platform = platform;
    return this.http.post<ScraperControlResponse>(`${this.base}/control`, body);
  }

  // ── Daily schedules ─────────────────────────────────────────────────────

  getSchedules(): Observable<{ schedules: ScraperSchedule[] }> {    return this.http.get<{ schedules: Record<string, unknown>[] }>(`${this.base}/schedules`)
      .pipe(map(r => ({ schedules: (r.schedules ?? []).map(s => this.normalizeSchedule(s)) })));
  }

  updateSchedule(platform: string, body: ScheduleUpdateBody): Observable<{ schedule: ScraperSchedule }> {
    return this.http.put<{ schedule: Record<string, unknown> }>(`${this.base}/schedules/${platform}`, body)
      .pipe(map(r => ({ schedule: this.normalizeSchedule(r.schedule) })));
  }

  runSchedule(platform: string): Observable<{ job_id: string; status: string; platform: string }> {
    return this.http.post<{ job_id: string; status: string; platform: string }>(
      `${this.base}/schedules/${platform}/run`, {},
    );
  }

  probeSchedule(platform: string): Observable<{ message: string; platform: string; job_id: string }> {
    return this.http.post<{ message: string; platform: string; job_id: string }>(
      `${this.base}/schedules/${platform}/probe`, {},
    );
  }

  deleteSchedule(platform: string): Observable<{ message: string; platform: string }> {
    return this.http.delete<{ message: string; platform: string }>(`${this.base}/schedules/${platform}`);
  }

  private normalizeSchedule(raw: Record<string, unknown>): ScraperSchedule {
    return {
      platform: (raw['platform'] as string) ?? (raw['_id'] as string) ?? '',
      enabled: raw['enabled'] as boolean,
      time: (raw['time'] as string) ?? '02:30',
      phase: (raw['phase'] as SchedulePhase) ?? 'destinations',
      kind: (raw['kind'] as ScheduleKind) ?? 'scheduled',
      probe_url: (raw['probe_url'] as string | null) ?? null,
      next_run: (raw['next_run'] as string | null) ?? null,
      last_run: (raw['last_run'] as string | null) ?? null,
      last_run_status: (raw['last_run_status'] as ScheduleRunStatus) ?? null,
      last_job_id: (raw['last_job_id'] as string | null) ?? null,
      last_error: (raw['last_error'] as string | null) ?? null,
      created_at: (raw['created_at'] as string) ?? '',
      updated_at: (raw['updated_at'] as string) ?? '',
    };
  }
}
